// apps/share-web/src/text-share/content-sync.ts
//
// Стык «ревизия сказала, что устарело → привести страницу» (спека
// docs/superpowers/specs/2026-09-17-live-content-revision-design.md §4.4).
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ ЗАМЫКАНИЕ В main.ts. Первая редакция жила
// именно замыканием, и сквозной ревью 17.09 показал цену: на этой границе
// оказались ТРИ дефекта сразу — вечный отказ при пропавшем заголовке, закрытие
// листа в обход `uncover()` и опознание песни только на прежней позиции, — и
// все три были зелёными, потому что граница не покрывалась ни одним тестом.
// Спека §6 называет тест этого стыка замком всей фичи; замок невозможен, пока
// стык не отделён от страницы.
//
// Всё наружу инъекцией: ни fetch, ни DOM здесь не знают.

import type { TextSharePayload } from '@bandway/share-types';
import type { TextShareResult } from './text-share-api.js';

export interface ContentSyncDeps {
  /** Ревизия, с которой страница загрузилась. `undefined` — воркер её не отдал. */
  initialRev: number | undefined;
  /** Перечитать нагрузку. В проде — `fetchTextShare` с тем же apiBase. */
  fetchPayload: () => Promise<TextShareResult>;
  /**
   * Та же ли это страница. Пока шёл запрос, читатель мог уйти на другую ссылку —
   * и применить к ней чужую нагрузку хуже, чем не применить ничего.
   */
  isCurrent: () => boolean;
  /** Отдать новую нагрузку поверхности (в проде — `view.setPayload`). */
  apply: (payload: TextSharePayload) => void;
}

export interface ContentSync {
  /**
   * Привести страницу к ревизии `rev`. Возврат — МОЖНО ЛИ ПРИМЕНЯТЬ индекс,
   * посчитанный по этой ревизии.
   */
  sync: (rev: number) => Promise<boolean>;
  /** Ревизия, которую страница держит сейчас. Для тестов и отладки. */
  heldRev: () => number | undefined;
}

export function createContentSync(deps: ContentSyncDeps): ContentSync {
  let held = deps.initialRev;

  return {
    heldRev: () => held,
    async sync(rev: number): Promise<boolean> {
      if (held === rev) return true;

      const fresh = await deps.fetchPayload();

      // Страницу успели сменить, пока шёл запрос: этот ответ уже ничей.
      if (!deps.isCurrent()) return false;

      // Завершение и исчезновение ссылки — НЕ забота этой функции: она отвечает
      // на «свежо ли содержимое». Объявить страницу мёртвой из фонового опроса
      // значило бы снести чтение под человеком по одному неудачному кругу.
      // Следующий круг вернётся сюда же.
      if (fresh.state !== 'ok') return false;

      deps.apply(fresh.payload);
      held = fresh.contentRev;

      // Заголовка в ответе не было вовсе — считаем, что привели.
      //
      // Найдено сквозным ревью 17.09. Строгое сравнение здесь означало бы вечное
      // `false`: `undefined` не равен никакому числу, якорь не применялся бы НИ
      // РАЗУ, кнопка Live не загоралась бы, а нагрузка перекачивалась и
      // перерисовывалась на КАЖДОМ опросе — и всё это без единой ошибки в
      // консоли. Путь достижим, как только `apiBase` уедет на другой origin:
      // `X-Content-Rev` тогда скрыт от JS, пока в ответе нет
      // `Access-Control-Expose-Headers`.
      if (held === undefined) return true;

      // Сервер назвал `rev`, а пришло другое — содержимое успели переписать ещё
      // раз между двумя запросами. Не применяем: следующий опрос принесёт
      // согласованную пару.
      return held === rev;
    },
  };
}
